/**
 * Static config + filter-tree helpers for the "Question Paper Create" tool.
 *
 * Question data now comes from the Medhabi Question Bank public API (see
 * ../../../../../services/questionBankService). This module only holds the
 * type constants and pure helpers that derive dropdown options from the
 * `/filters` response.
 *
 * Question shape (from the API): { id, class, subject, chapter, type, marks, text, options? }
 *   `type`   is one of QUESTION_TYPES ('mcq' | 'short' | 'broad').
 *   `class`  is a string ('3'..'10').
 *   `options` (MCQ only) is an array of choice strings.
 *
 * Filter-tree shape (from /filters):
 *   { classes: [{ value, subjects: [{ value, chapters: [] }] }] }
 */

export const QUESTION_TYPES = ['mcq', 'short', 'broad'];

// Marks conventionally attached to each type on a printed paper.
export const DEFAULT_MARKS = { mcq: 1, short: 2, broad: 5 };

// ── Filter-tree lookups (pure; operate on the /filters response) ─────
const bnDigit = (n) => String(n).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);

// Exam-level buckets (SSC/HSC) outrank numeric classes when ordering.
const classRank = (v) => (/^\d+$/.test(v) ? Number(v) : v === 'SSC' ? 1000 : v === 'HSC' ? 1001 : 2000);

/** Class dropdown options, highest level first (HSC, SSC, 10 → 3), with a Bangla-digit label. */
export function buildClassOptions(tree) {
    const classes = tree?.classes ?? [];
    return [...classes]
        .sort((a, b) => classRank(b.value) - classRank(a.value))
        .map((c) => ({ value: c.value, label: c.value, labelBn: bnDigit(c.value) }));
}

/** Subjects available for the given class. */
export function subjectsForClass(tree, cls) {
    if (!cls) return [];
    const cl = (tree?.classes ?? []).find((c) => c.value === cls);
    return (cl?.subjects ?? []).map((s) => s.value);
}

/** Chapters available for the given class + subject. */
export function chaptersFor(tree, cls, subject) {
    if (!cls || !subject) return [];
    const cl = (tree?.classes ?? []).find((c) => c.value === cls);
    const sub = (cl?.subjects ?? []).find((s) => s.value === subject);
    return sub?.chapters ?? [];
}

/**
 * Image URLs attached to a question. The API returns figures under `images`
 * (an array of absolute URLs); we also accept a few singular field names
 * defensively so a backend rename doesn't silently drop images.
 * @returns {string[]}
 */
export function questionImages(q) {
    if (!q) return [];
    const raw = q.images ?? q.imageUrl ?? q.image ?? q.figures ?? q.figure;
    const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return arr.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim());
}
